import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ThumbsUp, Heart, Laugh, Sparkles, MessageCircle, Share2, Trash2, Send, MoreHorizontal, Flag, VolumeX, Ban } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/components/Layout";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Input } from "@/components/ui/input";
import ReactionInsights from "@/components/ReactionInsights";

const REACTIONS = {
  like: { icon: ThumbsUp, color: "#8012C6", label: "Like" },
  love: { icon: Heart, color: "#E024A3", label: "Love" },
  laugh: { icon: Laugh, color: "#FFB800", label: "Haha" },
  wow: { icon: Sparkles, color: "#00A3B4", label: "Wow" },
};

export default function PostCard({ post }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { openReport, confirmAction } = useUI();
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [counts, setCounts] = useState(post.reaction_counts || {});
  const [total, setTotal] = useState(post.reaction_total || 0);
  const [showPicker, setShowPicker] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showReactors, setShowReactors] = useState(false);
  const [comment, setComment] = useState("");
  const hideTimer = useRef(null);

  const author = post.author || {};

  const { data: comments = [], refetch } = useQuery({
    queryKey: ["comments", post.id],
    queryFn: async () => (await api.get(`/posts/${post.id}/comments`)).data,
    enabled: showComments,
  });

  const react = async (type) => {
    setShowPicker(false);
    try {
      const { data } = await api.post(`/posts/${post.id}/react`, { type });
      setMyReaction(data.my_reaction);
      setCounts(data.reaction_counts);
      setTotal(data.reaction_total);
    } catch {}
  };

  const onMainButton = () => react(myReaction || "like");

  const enter = () => { clearTimeout(hideTimer.current); setShowPicker(true); };
  const leave = () => { hideTimer.current = setTimeout(() => setShowPicker(false), 250); };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    const c = comment;
    setComment("");
    try {
      await api.post(`/posts/${post.id}/comments`, { content: c });
    } catch (err) {
      setComment(c);
      toast.error(formatApiError(err.response?.data?.detail));
      return;
    }
    refetch();
    qc.invalidateQueries({ queryKey: ["feed"] });
  };

  const del = async () => {
    await api.delete(`/posts/${post.id}`);
    qc.invalidateQueries({ queryKey: ["feed"] });
    qc.invalidateQueries({ queryKey: ["user-posts"] });
  };

  const relate = async (kind) => {
    if (kind === "block" && !(await confirmAction({ title: `Block ${author.name}?`, description: "They won't be able to see your posts, message, call or friend you. They won't be notified.", actionLabel: "Block" }))) return;
    try {
      await api.post(`/users/${author.user_id}/${kind}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return;
    }
    toast.success(kind === "block" ? `${author.name} blocked` : `${author.name} muted — you won't see their posts`);
    qc.invalidateQueries({ queryKey: ["feed"] });
    qc.invalidateQueries({ queryKey: ["stories"] });
    qc.invalidateQueries({ queryKey: ["profile", author.user_id] });
    qc.invalidateQueries({ queryKey: ["relations"] });
  };

  const Current = myReaction ? REACTIONS[myReaction] : REACTIONS.like;
  const topReactions = Object.keys(counts).filter((k) => counts[k] > 0).slice(0, 3);

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`post-${post.id}`}
      className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:border-[#6C0BA9]/40 transition-all"
    >
      <div className="flex items-center gap-3">
        <Avatar user={author} size={44} onClick={() => author.user_id && navigate(`/profile/${author.user_id}`)} />
        <div className="flex-1 min-w-0">
          <button onClick={() => author.user_id && navigate(`/profile/${author.user_id}`)} className="font-semibold text-sm flex items-center gap-1 hover:underline">
            {author.name}{author.verified && <VerifiedBadge size={14} />}
          </button>
          <div className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</div>
        </div>
        {author.user_id === user.user_id ? (
          <button data-testid={`delete-post-${post.id}`} onClick={del} className="w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-red-400 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`post-menu-${post.id}`} className="w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center text-muted-foreground transition-colors">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-popover border-border">
              <DropdownMenuItem data-testid={`report-post-${post.id}`} onClick={() => openReport({ type: "post", id: post.id, label: "post" })} className="cursor-pointer"><Flag className="w-4 h-4 mr-2" />Report post</DropdownMenuItem>
              <DropdownMenuItem data-testid={`mute-author-${post.id}`} onClick={() => relate("mute")} className="cursor-pointer"><VolumeX className="w-4 h-4 mr-2" />Mute {author.name}</DropdownMenuItem>
              <DropdownMenuItem data-testid={`block-author-${post.id}`} onClick={() => relate("block")} className="cursor-pointer text-red-400"><Ban className="w-4 h-4 mr-2" />Block {author.name}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {post.content && <p className="mt-3 text-[15px] leading-relaxed whitespace-pre-wrap">{post.content}</p>}
      {post.image_url && (
        <div className="mt-3 rounded-xl overflow-hidden border border-border">
          <img src={mediaUrl(post.image_url)} alt="" className="w-full max-h-[520px] object-cover" />
        </div>
      )}

      {total > 0 && (
        <button
          data-testid={`reaction-summary-${post.id}`}
          onClick={() => setShowReactors(true)}
          className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground hover:underline"
        >
          <div className="flex -space-x-1">
            {topReactions.map((k) => {
              const R = REACTIONS[k]; const Icon = R.icon;
              return (
                <span key={k} className="w-5 h-5 rounded-full flex items-center justify-center border-2 border-card" style={{ background: R.color }}>
                  <Icon className="w-3 h-3 text-white" style={k === "love" ? { fill: "white" } : {}} />
                </span>
              );
            })}
          </div>
          {total}
        </button>
      )}

      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
        <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
          <AnimatePresence>
            {showPicker && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.9 }}
                data-testid={`reaction-picker-${post.id}`}
                className="absolute bottom-full mb-2 left-0 flex items-center gap-1 bg-popover border border-border rounded-full px-2 py-1.5 shadow-2xl z-20"
              >
                {Object.entries(REACTIONS).map(([key, R]) => {
                  const Icon = R.icon;
                  return (
                    <button
                      key={key}
                      data-testid={`react-${key}-${post.id}`}
                      onClick={() => react(key)}
                      title={R.label}
                      className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-125 transition-transform"
                      style={{ background: `${R.color}22` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: R.color, fill: key === "love" ? R.color : "none" }} />
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
          <button
            data-testid={`react-btn-${post.id}`}
            onClick={onMainButton}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all hover:bg-accent`}
            style={myReaction ? { color: Current.color } : {}}
          >
            <Current.icon className="w-5 h-5" style={myReaction ? { color: Current.color, fill: myReaction === "love" ? Current.color : "none" } : {}} />
            {myReaction ? Current.label : "React"}
          </button>
        </div>
        <button data-testid={`comment-btn-${post.id}`} onClick={() => setShowComments((s) => !s)} className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm text-muted-foreground hover:bg-accent transition-colors">
          <MessageCircle className="w-5 h-5" /> {post.comment_count > 0 && post.comment_count}
        </button>
        <button data-testid={`share-btn-${post.id}`} className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm text-muted-foreground hover:bg-accent transition-colors ml-auto">
          <Share2 className="w-5 h-5" />
        </button>
      </div>

      {showComments && (
        <div className="mt-3 space-y-3">
          <form onSubmit={addComment} className="flex gap-2">
            <Avatar user={user} size={32} />
            <Input data-testid={`comment-input-${post.id}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment..." className="rounded-full bg-input border-border h-9" />
            <button data-testid={`comment-send-${post.id}`} type="submit" className="w-9 h-9 shrink-0 rounded-full bg-[#6C0BA9] hover:bg-[#8012C6] text-white flex items-center justify-center"><Send className="w-4 h-4" /></button>
          </form>
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2 group" data-testid={`comment-${c.id}`}>
              <Avatar user={c.author} size={32} onClick={() => navigate(`/profile/${c.author.user_id}`)} />
              <div className="bg-secondary rounded-2xl px-3.5 py-2">
                <div className="text-xs font-semibold flex items-center gap-1">{c.author?.name}{c.author?.verified && <VerifiedBadge size={11} />}</div>
                <div className="text-sm">{c.content}</div>
              </div>
              {c.author?.user_id !== user.user_id && (
                <button data-testid={`report-comment-${c.id}`} onClick={() => openReport({ type: "comment", id: c.id, label: "comment" })} title="Report comment" className="self-center opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-[#E024A3] transition-opacity">
                  <Flag className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <ReactionInsights postId={post.id} open={showReactors} onClose={() => setShowReactors(false)} />
    </motion.article>
  );
}
